import { CommonModule } from '@angular/common';
import { Component, ChangeDetectionStrategy, Input, OnChanges } from '@angular/core';

import { ButtonComponent, IconComponent, TooltipDirective } from '@mode/capra';
import { DatasetUsageDialogFacade } from '@mode/dataset/usage/contract';
import { pluralize } from '@mode/shared/util-js';

@Component({
  standalone: true,
  selector: 'mode-query-dependencies',
  imports: [ButtonComponent, CommonModule, IconComponent, TooltipDirective],
  templateUrl: './query-dependencies.component.html',
  styleUrls: ['./query-dependencies.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QueryDependenciesComponent implements OnChanges {
  @Input({ required: true }) explorationsCount = 0;
  @Input({ required: true }) reportsCount = 0;
  @Input({ required: true }) datasetsReportType = false;
  @Input() theme = 'light';

  tooltipCopy = '';
  dependencyCopy = '';
  totalCount = 0;

  constructor(private _datasetUsageDialogFacade: DatasetUsageDialogFacade) {}

  ngOnChanges(): void {
    this.tooltipCopy = this._generateTooltipCopy();
    this.dependencyCopy = this._pluralizeDependencies(
      this.explorationsCount,
      this.reportsCount,
      this.datasetsReportType
    );
    this.totalCount = this.explorationsCount + this.reportsCount;
  }

  openDatasetUsageModal() {
    this._datasetUsageDialogFacade.openDatasetUsageModal(this.reportsCount);
  }

  private _pluralizeDependencies(explorationsCount: number, reportsCount: number, datasetsReportType: boolean): string {
    const count = datasetsReportType ? explorationsCount + reportsCount : explorationsCount;
    if (datasetsReportType) {
      return 'View usage: ' + pluralize(count, 'Report', 'Reports');
    }
    return pluralize(count, 'dependency', 'dependencies');
  }

  private _generateTooltipCopy(): string {
    const explorationString = pluralize(this.explorationsCount, 'Exploration');
    const reportString = pluralize(this.reportsCount, 'Report');
    const reportsAndExplorationsExist = this.explorationsCount > 0 && this.reportsCount > 0;
    const verb = !reportsAndExplorationsExist && this.reportsCount + this.explorationsCount === 1 ? 'uses' : 'use';
    if (this.explorationsCount > 0 && !this.datasetsReportType) {
      return `${explorationString}
      reference the data returned from this query. Changes may impact the Explorations.`;
    }
    if ((explorationString || reportString) && this.datasetsReportType) {
      return `${explorationString} ${reportsAndExplorationsExist ? 'and' : ''} ${reportString} ${verb}
      this Dataset and may be impacted by changes to its data or access settings`;
    } else if (this.explorationsCount < 1 && !this.datasetsReportType) {
      return 'No explorations reference the data returned from this query.';
    }
  }
}
